<template>
  <div class="game-category">
    <atomic-page-title @on-click="router.back()"> {{ title }} </atomic-page-title>

    <slider-tabs
      v-if="activeProvider"
      :activeValue="activeCollection?.name"
      :items="collectionViews"
      @clickItem="changeCollection"
    />

    <div class="game-category-filters">
      <form-input-search
        v-model:value="searchValue"
        class="game-category-filters__search"
        :placeholder="getContent(layoutData, defaultLocaleLayoutData, 'header.search.placeholder')"
        :hasClearIcon="!!searchValue"
        @input="searchInput"
      />

      <bottom-sheet-games-filter
        v-if="isMobile"
        :isActive="isActiveFilter"
        :featuresOptions="featuresWithIdentity"
        :providersOptions="getFilteredProviders"
        :sortBy="sortBy"
        :sortOrder="sortOrder"
        :sortOptions="sortOptions"
        :hideProviders="!!activeProvider"
      />

      <template v-else>
        <filters-games-filter :isActive="isActiveFilter" :hideProviders="!!activeProvider" />

        <sorting-games-sorting :sortBy="sortBy" :sortOrder="sortOrder" :sortOptions="sortOptions" />
      </template>
    </div>

    <list-skeleton-preloader v-if="loadingGames" />

    <list-grid
      v-if="gameItems.length && !loadingGames"
      :items="gameItems"
      :meta="pageMeta"
      @load-more="loadMoreItems"
    />

    <atomic-custom-empty
      v-if="!gameItems.length && !loadingGames"
      :title="getContent(layoutData, defaultLocaleLayoutData, 'empty.nothingFound.title')"
      :description="getContent(layoutData, defaultLocaleLayoutData, 'empty.nothingFound.description')"
      :image="getContent(layoutData, defaultLocaleLayoutData, 'empty.nothingFound.img')"
    />

    <div class="game-category-providers" v-if="activeProvider">
      <group-providers show-arrows show-all-btn />

      <group-games-adaptive
        v-if="allGameCollection"
        :key="`desk-${allGameCollection.identity}`"
        :searchValue="searchValue"
        showAllBtn
        showArrows
        :category="allGameCollection"
        icon-color="blue"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import debounce from 'lodash/debounce';
  import { urlFormat } from '@skeleton/utils/routerUtils';
  import { normalizeToArray } from '@skeleton/utils/arrayUtils';
  import { isModalQueryChanged } from '@skeleton/utils/modalQueryUtils';

  import type { ICollection, IGame, IGamesRequest, IPaginationMeta, IGameProvider } from '@skeleton/core/types';

  const globalStore = useGlobalStore();
  const { layoutData, defaultLocaleLayoutData, headerCountry, gameCategoriesObj } = storeToRefs(globalStore);
  const deviceStore = useDeviceStore();
  const { isMobile } = storeToRefs(deviceStore);
  const { getContent } = useProjectMethods();
  const { getCollectionsList, getCollectionsRequest, findGameCollectionByIdentity } = useGamesStore();
  const { getGames } = useGameCollectionStoreStore();

  const gameFeaturesStore = useGameFeaturesStore();
  const { cachedFeatures, featuresWithIdentity } = storeToRefs(gameFeaturesStore);

  const gameProvidersStore = useGameProvidersStore();
  const { getFilteredProviders } = storeToRefs(gameProvidersStore);

  const { content: gamesContent } = useGamesContent();
  const { data: gameCollections } = await useLazyAsyncData(() => getCollectionsList(), { server: false });

  const { sortOptions, parseSortQuery } = await useSortingOptions();

  const route = useRoute();
  const router = useRouter();

  const sortBy = ref<string>();
  const sortOrder = ref<string>();

  const activeCollection = ref<ICollection>();
  const activeProvider = ref<Maybe<IGameProvider>>();
  const selectedProviders = ref<IGameProvider[]>([]);
  const selectedFeatures = ref<IGameProvider[]>([]);
  const allGameCollection = computed(() => getGameCollection('All'));

  const searchValue = ref('');
  const loadPage = ref(1);
  const gameItems = ref<IGame[]>([]);
  const pageMeta = ref<IPaginationMeta>();
  const loadingGames = ref(true);
  const MOBILE_ITEMS_PER_PAGE = 24;
  const DESKTOP_ITEMS_PER_PAGE = 72;
  const HEIGHT_THRESHOLD = 1000;

  const itemsPerPage = computed(() =>
    isMobile.value && window.innerHeight < HEIGHT_THRESHOLD ? MOBILE_ITEMS_PER_PAGE : DESKTOP_ITEMS_PER_PAGE
  );

  const title = computed(() => {
    if (activeProvider.value) {
      return activeProvider.value.name;
    }

    const { params = [] } = route.params;
    const defaultTitle = urlFormat(params[0]);

    if (!activeCollection.value) {
      return defaultTitle;
    }

    const categoryLabel = gameCategoriesObj.value[activeCollection.value.identity]?.label;
    return categoryLabel || activeCollection.value.name || activeCollection.value.identity || defaultTitle;
  });

  useHead({
    title:
      getContent(gamesContent.value?.currentLocaleData, gamesContent.value?.defaultLocaleData, 'IPageMeta.title') +
      ' | ' +
      title.value,
  });

  const isActiveFilter = computed(() => !!selectedProviders.value.length || !!selectedFeatures.value.length);

  const filteredCollection = computed(() => gameCollections.value?.filter(collection => !collection.isHidden));

  const collectionViews = computed(() => {
    return (
      filteredCollection.value?.map(item => {
        const findCollection = gameCategoriesObj.value[item.identity.toLowerCase()];

        return {
          ...item,
          ...findCollection,
          label: findCollection?.label || item.name,
        };
      }) || []
    );
  });

  const getGameCollection = (identity: string): ICollection | undefined => {
    return findGameCollectionByIdentity(filteredCollection.value || [], identity);
  };

  const providerIds = computed(() => {
    if (activeProvider.value?.id) {
      return [activeProvider.value.id];
    }

    if (selectedProviders.value.length) {
      return selectedProviders.value.map(item => item.id);
    }

    return undefined;
  });

  const defaultRequestParams = computed<IGamesRequest>(() => ({
    page: loadPage.value,
    perPage: itemsPerPage.value,
    collectionId: activeCollection.value?.id ? [activeCollection.value?.id] : undefined,
    sortBy: sortBy.value,
    sortOrder: sortOrder.value,
    countries: headerCountry.value ? [headerCountry.value] : undefined,
    name: searchValue.value || undefined,
    providerId: providerIds.value,
    features: selectedFeatures.value.length ? selectedFeatures.value : undefined,
  }));

  const searchInput = debounce(async () => {
    if (searchValue.value.length > 1 || !searchValue.value) {
      loadingGames.value = true;
      changePage(1);
      await loadGames();
    }
  }, 500);

  const updateQueryWithReplace = () => {
    router.replace({
      query: {
        ...route.query,
        providers: selectedProviders.value.length ? selectedProviders.value.map(item => item.identity) : undefined,
        features: selectedFeatures.value.length
          ? selectedFeatures.value.map(item => encodeURIComponent(item))
          : undefined,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
      },
    });
  };

  const changeCollection = (item: ICollection) => {
    activeCollection.value = item;
    loadingGames.value = true;
    changePage(1);
    loadGames();
  };

  const setActiveProviders = () => {
    const { providers } = route.query;

    const providerList = normalizeToArray(providers);
    selectedProviders.value =
      getFilteredProviders.value.filter(provider => providerList.includes(provider.identity)) || [];
  };

  const setActiveFeatures = () => {
    const { features } = route.query;

    const featuresList = normalizeToArray(features);
    const decodeFeaturesList = featuresList.map(item => decodeURIComponent(item));
    selectedFeatures.value = cachedFeatures.value.filter(feature => decodeFeaturesList.includes(feature)) || [];
  };

  const setActiveProviderCollection = async () => {
    const { params = [] } = route.params;

    const findProvider = getFilteredProviders.value.find(item => item.identity === params[0]);
    activeProvider.value = findProvider ?? null;

    if (activeProvider.value) {
      await loadCollections(activeProvider.value.id);
      return;
    }

    const findCollection = gameCollections.value?.find(
      item => item.identity.toUpperCase() === urlFormat(params[0]).toUpperCase()
    );

    activeCollection.value = findCollection || gameCollections.value?.[0];
  };

  const setActiveSort = () => {
    const parsedQuery = parseSortQuery(route.query);
    sortOrder.value = parsedQuery.sortOrder;
    sortBy.value = parsedQuery.sortBy;
  };

  const setItems = (data: any, more?: boolean) => {
    gameItems.value = more ? gameItems.value.concat(data.games) : data.games;
    pageMeta.value = data.pageMeta;
  };

  const changePage = (newPage: number = 1): void => {
    loadPage.value = newPage;
  };

  const setDefaultCollection = () => {
    activeCollection.value = allGameCollection.value;
  };

  const loadMoreItems = async () => {
    const newPage = (pageMeta.value?.page || 0) + 1;
    changePage(newPage);
    await loadGames(true);
  };

  const loadGames = async (more = false) => {
    const data = await getGames(defaultRequestParams.value, true);
    setItems(data, more);
    loadingGames.value = false;
  };

  const loadCollections = async (providerId: string): Promise<void> => {
    gameCollections.value = await getCollectionsRequest({ providerId });
    setDefaultCollection();
  };

  const loadProviders = async () => {
    await gameProvidersStore.getProviders();
    setActiveProviders();
  };

  const loadFeatures = async () => {
    await gameFeaturesStore.getFeatures();
    setActiveFeatures();
  };

  watch(
    () => route.query,
    (newQuery, oldQuery) => {
      if (!Object.keys(oldQuery).length) return;
      if (isModalQueryChanged(newQuery, oldQuery)) return;

      loadingGames.value = true;
      setActiveProviders();
      setActiveFeatures();
      setActiveSort();
      changePage(1);
      loadGames();
    }
  );

  watchEffect(() => {
    setActiveProviderCollection();
  });

  onMounted(async () => {
    setActiveSort();
    await loadProviders();
    await loadFeatures();
    await loadGames();
    updateQueryWithReplace();
  });
</script>

<style src="~/assets/styles/pages/games/game-category.scss" lang="scss" />
